import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Eye, MoreVertical, Pencil } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import {
  ruleRows,
} from "../consts";
import { useGetCommunityFinancialInfo, useGetDistributionRuleMethodStatusMatrix } from "@/services";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { activeLevels, ACTUALS, actualTab, COMMUNITY_TO_ZONE, FIXED_WEIGHTS, PLANNED, plannedTab, reportingLevelOptions, WEIGHING_FACTORS } from "@/lib/utils";
import { RuleBudgetType } from "./types";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "@/routes/utils";

const useDistributionRulesByLevel = () => {
  const navigate =  useNavigate();
  const location = useLocation();
  const fromTraceability = location?.pathname?.includes(routes.traceability);
  const [activeStatusTab, setActiveStatusTab] = useState<RuleBudgetType>(fromTraceability ? ACTUALS : PLANNED);
  const [activeLevel, setActiveLevel] = useState<string>(COMMUNITY_TO_ZONE);
  const [openConfigureDialog, setOpenConfigureDialog] = useState<boolean>(false);
  const configuredData = useRef<any>(null);

  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {data: communityRules, isLoading: isLoadingRules } = useGetDistributionRuleMethodStatusMatrix({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  })

  const {data: financialInfo, isLoading: isLoadingFinancialInfo } = useGetCommunityFinancialInfo({
    community_id: communityData?.communityId,
    queryParams: {
      enabled: !!(communityData?.communityId)
    }
  })

  const ruleStatusTabs = useMemo(() => {
    const distributionMode = financialInfo?.data?.distributionMode;
    if (fromTraceability) {
      return actualTab;
    } else if (distributionMode == PLANNED){
      return plannedTab;
    } else if (distributionMode == ACTUALS) {
      return actualTab;
    } else {
      return [...plannedTab, ...actualTab];
    }
  }, [financialInfo, fromTraceability]);

  const activeLevelIndex = useMemo(() => activeLevels.findIndex(
    (level) => level.level == activeLevel
  ), [activeLevel])

  const tableData = useMemo(() => {
    return ruleRows?.map((data: any) => {
      let rules = {};

      if (data?.key == FIXED_WEIGHTS) {
        const ruleExist = communityRules?.data?.find((val: any) => val?.method == data?.key && val?.level == activeLevel && val?.budgetType == activeStatusTab)
        rules = {...ruleExist, configured: !!ruleExist}
      } else if (data?.key == WEIGHING_FACTORS) {
        const ruleExist = communityRules?.data?.find((val: any) => val?.method == data?.key && val?.budgetType == activeStatusTab)
        rules = {...ruleExist, configured: !!ruleExist}
      } else {
        rules = communityRules?.data?.find((val: any) => val?.method == data?.key)
      }
      return {
        ...data,
        ...rules,
        index: activeLevelIndex
      };
    });
  }, [communityRules, activeLevel, activeStatusTab, activeLevelIndex, ruleRows]);

  const activeThresholdIndex = useMemo(() => activeLevels.findIndex(
    (level) => level.value == financialInfo?.data?.distributionLevel
  ), [financialInfo])

  const columns = [
    {
      accessorKey: "label",
      header: "Rule",
      size: 260,
    },
    {
      accessorKey: "configured",
      header: "",
      size: 260,
      cell: ({ row }: any) => {
        const rowData = row.original as any;

        return rowData?.configured || rowData?.key == "EQUAL_DISTRIBUTION" ? (
          <div className="flex w-full items-center justify-center">
            <CheckCircle2 className="h-5 w-5 fill-green-600 text-white" />
          </div>
        ) : (
          <div className="flex w-full items-center justify-center text-center text-red-500">
            {rowData?.statusLabel}
          </div>
        );
      },
    },
    ...(activeThresholdIndex != -1 ? 
      [{
        id: "actions",
        header: <div className="flex w-full justify-center">Actions</div>,
        size: 180,
        cell: ({ row }: any) => {
          const rowData = row.original as any;

          return (
            <div className="flex w-full justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <MoreVertical className="w-5 h-5 cursor-pointer text-g-500" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="min-w-[130px] bg-white border-0 shadow-md"
                >
                  <DropdownMenuItem
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (rowData?.key == FIXED_WEIGHTS) {
                        navigate(`${routes.fixedWeight}/${activeLevel}/${activeStatusTab}/${routes.view}`);
                      } if (rowData?.key == WEIGHING_FACTORS) { 
                        navigate(`${routes.weighingFactor}/${activeStatusTab}`);
                      } else {
                        onConfigureClick(rowData, "view");
                      }
                    }}
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </DropdownMenuItem>
                  {rowData?.key == FIXED_WEIGHTS && !fromTraceability ? <DropdownMenuItem
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`${routes.fixedWeight}/${activeLevel}/${activeStatusTab}/${routes.edit}`);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                    Configure
                  </DropdownMenuItem> : <></>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      }] : []),
  ];
  
  const currentReportingLevelLabel = useMemo(() => reportingLevelOptions.find((data: any) => data.value == financialInfo?.data?.distributionLevel)?.label, [financialInfo]);
  
  const onLevelChange = (level: string) => () => {
    setActiveLevel(level);
  }

  const onConfigureClick = (row: any, action: "view" | "edit") => {
    configuredData.current = {...row, action};
    setOpenConfigureDialog(true);
  };

  const onStatusTabChange = (value: any) => {
    setActiveStatusTab(value)
  }

  return {
    isLoadingRules,
    isLoadingFinancialInfo,
    currentReportingLevelLabel,
    activeThresholdIndex,
    openConfigureDialog,
    setOpenConfigureDialog,
    configuredData: configuredData.current,
    columns,
    tableData,
    activeStatusTab,
    activeLevel,
    activeLevelIndex,
    onLevelChange,
    setActiveStatusTab,
    onStatusTabChange,
    ruleStatusTabs
  };
};

export default useDistributionRulesByLevel;
